import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import type { Product } from "@acs/shared";
import { NewProductModal, OnboardingModal } from "./Sidebar";

// ── Yeni UI'nın kalıcı sidebar'ı ────────────────────────────────────────────
// ProductLayout'un tek navigasyon kabuğu — Tree/Formats/Galeri/Ayarlar dahil
// her yere buradan gidilir, hiçbir sayfa bu sidebar'ın dışına çıkmaz.
// Ürün seçimi de burada: logoya/isme tıklayınca açılan dropdown'dan.

const ACCENT = "#5E6AD2";

function st(css: string): React.CSSProperties {
  const obj: Record<string, string> = {};
  css.split(";").forEach((decl) => {
    const idx = decl.indexOf(":");
    if (idx === -1) return;
    const prop = decl.slice(0, idx).trim();
    const val = decl.slice(idx + 1).trim();
    if (!prop || !val) return;
    const camel = prop.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
    obj[camel] = val;
  });
  return obj as React.CSSProperties;
}

function H({
  tag: Tag = "div", baseStyle, hoverStyle, ...rest
}: { tag?: any; baseStyle: React.CSSProperties; hoverStyle?: React.CSSProperties; [k: string]: any }) {
  const [hover, setHover] = useState(false);
  return (
    <Tag
      {...rest}
      style={{ ...baseStyle, ...(hover ? hoverStyle : {}) }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    />
  );
}

const NAV_ITEMS = [
  { key: "dashboard", label: "Home" },
  { key: "studio", label: "Create" },
  { key: "tree", label: "Creative tree" },
  { key: "formats", label: "Formats" },
  { key: "assets", label: "Asset Library" },
  { key: "gallery", label: "Galeri" },
  { key: "performance", label: "Performance" },
  { key: "settings", label: "Ürün Ayarları" },
] as const;

function NavIcon({ k }: { k: string }) {
  const common = { width: 17, height: 17, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.7, strokeLinecap: "round" as const, strokeLinejoin: "round" as const };
  if (k === "dashboard") return <svg {...common}><path d="M3 10.5 12 3l9 7.5" /><path d="M5 9.5V20h14V9.5" /></svg>;
  if (k === "studio") return <svg {...common}><path d="M12 4l1.4 4.8L18 10l-4.6 1.2L12 16l-1.4-4.8L6 10l4.6-1.2z" /></svg>;
  if (k === "tree") return <svg {...common}><circle cx="6" cy="6" r="2.3" /><circle cx="6" cy="18" r="2.3" /><circle cx="18" cy="12" r="2.3" /><path d="M8.3 6H13a2.5 2.5 0 0 1 2.5 2.5V10M8.3 18H13a2.5 2.5 0 0 0 2.5-2.5V14" /></svg>;
  if (k === "formats") return <svg {...common}><rect x="3.5" y="3.5" width="7" height="7" rx="1.6" /><rect x="13.5" y="3.5" width="7" height="7" rx="1.6" /><rect x="3.5" y="13.5" width="7" height="7" rx="1.6" /><rect x="13.5" y="13.5" width="7" height="7" rx="1.6" /></svg>;
  if (k === "gallery") return <svg {...common}><rect x="3.5" y="3.5" width="17" height="17" rx="2.5" /><circle cx="8.5" cy="9" r="1.5" /><path d="M4 16l4.5-4.5 3 3 5-5L20 14" /></svg>;
  if (k === "assets") return <svg {...common}><rect x="3.5" y="6" width="17" height="12" rx="2" /><path d="M3.5 10h17M8 6V4.5h8V6" /></svg>;
  if (k === "performance") return <svg {...common}><path d="M4 20V10M11 20V4M18 20v-7" /></svg>;
  return <svg {...common}><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.6V21a2 2 0 1 1-4 0v-.2a1.7 1.7 0 0 0-1-1.5 1.7 1.7 0 0 0-1.9.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.9 1.7 1.7 0 0 0-1.6-1H3a2 2 0 1 1 0-4h.2a1.7 1.7 0 0 0 1.5-1 1.7 1.7 0 0 0-.3-1.9l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.9.3H9a1.7 1.7 0 0 0 1-1.6V3a2 2 0 1 1 4 0v.2a1.7 1.7 0 0 0 1 1.6 1.7 1.7 0 0 0 1.9-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.9V9a1.7 1.7 0 0 0 1.6 1H21a2 2 0 1 1 0 4h-.2a1.7 1.7 0 0 0-1.6 1z" /></svg>;
}

export function StudioSidebar({
  productId, productName, products, onProductsChanged, onLogout,
}: {
  productId: string;
  productName?: string;
  products: Product[];
  onProductsChanged: () => Promise<void> | void;
  onLogout?: () => void;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [onboardingId, setOnboardingId] = useState<string | null>(null);
  const switcherRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!switcherOpen) return;
    function onDocClick(e: MouseEvent) {
      if (switcherRef.current && !switcherRef.current.contains(e.target as Node)) setSwitcherOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [switcherOpen]);

  return (
    <aside style={st("width:246px;flex:none;display:flex;flex-direction:column;background:#fbfbfc;border-right:1px solid rgba(20,20,30,.08);padding:16px 12px;position:relative")}>
      <div ref={switcherRef} style={st("position:relative")}>
        <H onClick={() => setSwitcherOpen((o) => !o)}
          baseStyle={st("display:flex;align-items:center;gap:10px;padding:6px 8px;margin-bottom:12px;border-radius:8px;cursor:pointer")}
          hoverStyle={st("background:rgba(20,20,30,.05)")}
        >
          <div style={{ ...st("width:28px;height:28px;border-radius:7px;display:flex;align-items:center;justify-content:center;flex:none"), background: ACCENT }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="#fff"><path d="M12 3l1.7 6.1L20 11l-6.3 1.9L12 19l-1.7-6.1L4 11l6.3-1.9z" /></svg>
          </div>
          <div style={st("line-height:1.15;min-width:0;flex:1")}>
            <div style={st("font-size:13.5px;font-weight:600;letter-spacing:-.01em;white-space:nowrap;overflow:hidden;text-overflow:ellipsis")}>{productName ?? "Yükleniyor…"}</div>
            <div style={st("font-size:11px;color:#9a9aa2")}>Creative Studio</div>
          </div>
          <span style={{ ...st("font-size:9px;color:#9a9aa2;transition:transform .15s"), transform: switcherOpen ? "rotate(180deg)" : "none" }}>▼</span>
        </H>

        {switcherOpen && (
          <div style={st("position:absolute;top:100%;left:0;right:0;z-index:30;background:#fff;border:1px solid rgba(20,20,30,.1);border-radius:10px;box-shadow:0 8px 24px rgba(20,20,30,.12);padding:6px;margin-top:2px;max-height:340px;overflow-y-auto")}>
            {products.map((p) => {
              const active = p.id === productId;
              return (
                <H key={p.id} tag="button" onClick={() => { setSwitcherOpen(false); navigate(`/products/${p.id}/dashboard`); }}
                  baseStyle={{ ...st("display:block;width:100%;text-align:left;padding:8px 10px;border:none;border-radius:7px;font-family:inherit;cursor:pointer"), background: active ? `color-mix(in srgb,${ACCENT} 10%,transparent)` : "transparent" }}
                  hoverStyle={st("background:rgba(20,20,30,.05)")}
                >
                  <div style={{ ...st("font-size:13px;font-weight:500"), color: active ? ACCENT : "#2a2a33" }}>{p.name}</div>
                  <div style={st("font-size:11px;color:#9a9aa2")}>{p.tag}</div>
                </H>
              );
            })}
            <div style={st("height:1px;background:rgba(20,20,30,.07);margin:5px 2px")} />
            <H tag="button" onClick={() => { setSwitcherOpen(false); setShowNew(true); }}
              baseStyle={{ ...st("display:block;width:100%;text-align:left;padding:8px 10px;border:none;border-radius:7px;font-family:inherit;font-size:13px;font-weight:500;cursor:pointer"), color: ACCENT }}
              hoverStyle={{ background: `color-mix(in srgb,${ACCENT} 8%,#fff)` }}
            >
              + Yeni Ürün
            </H>
            {onLogout && (
              <H tag="button" onClick={() => { setSwitcherOpen(false); onLogout(); }}
                baseStyle={st("display:block;width:100%;text-align:left;padding:8px 10px;border:none;border-radius:7px;font-family:inherit;font-size:13px;cursor:pointer;color:#8a8a93")}
                hoverStyle={st("background:rgba(20,20,30,.05)")}
              >
                ↩ Çıkış Yap
              </H>
            )}
          </div>
        )}
      </div>

      {showNew && (
        <NewProductModal
          onClose={() => setShowNew(false)}
          onSaved={async (newId) => {
            setShowNew(false);
            await onProductsChanged();
            setOnboardingId(newId);
          }}
        />
      )}
      {onboardingId && (
        <OnboardingModal
          productId={onboardingId}
          onClose={() => {
            const id = onboardingId;
            setOnboardingId(null);
            navigate(`/products/${id}/settings`);
          }}
        />
      )}

      <nav style={st("display:flex;flex-direction:column;gap:2px")}>
        {NAV_ITEMS.map(({ key, label }) => {
          const active = location.pathname.endsWith(`/${key}`);
          const bg = active ? `color-mix(in srgb,${ACCENT} 12%,transparent)` : "transparent";
          const fg = active ? ACCENT : "#5b5b66";
          return (
            <H key={key} tag="button" onClick={() => navigate(`/products/${productId}/${key}`)}
              baseStyle={{ ...st("display:flex;align-items:center;gap:11px;width:100%;padding:8px 11px;border:none;border-radius:7px;font-family:inherit;font-size:13.5px;font-weight:500;cursor:pointer;text-align:left;transition:background .12s,color .12s"), background: bg, color: fg }}
              hoverStyle={st("background:rgba(20,20,30,.05)")}
            >
              <NavIcon k={key} />
              {label}
            </H>
          );
        })}
      </nav>

      <div style={st("margin-top:auto")}>
        <div style={st("display:flex;align-items:center;gap:9px;padding:8px 9px;border-top:1px solid rgba(20,20,30,.07)")}>
          <div style={st("width:26px;height:26px;border-radius:50%;background:linear-gradient(135deg,#d6d9ef,#b9bfe4);flex:none")} />
          <div style={st("line-height:1.2")}>
            <div style={st("font-size:12.5px;font-weight:500")}>Marketing team</div>
            <div style={st("font-size:11px;color:#9a9aa2")}>Teknasyon</div>
          </div>
        </div>
      </div>
    </aside>
  );
}
